import {
  connectMainSocket,
  getMainSocket,
  onSocketAuthenticated,
  queueEmitUntilConnected
} from '../../clients/mainSocketClient'
import { MAIN_SOCKET_API_EVENTS } from '../../constants/api/mainSocket'

const coordinatesCreatedEvent = MAIN_SOCKET_API_EVENTS.COORDINATES_CREATED

/**
 * Map real-time socket service.
 * Contract for backend: see docs/socket/MAP_SOCKET_CONTRACT.md
 *
 * Methods to implement on the socket.io server:
 * - Room: map (join on MAP_JOIN, leave on MAP_LEAVE)
 * - Emit to clients in map room: MAP_DEVICE_POSITION_UPDATED, MAP_DEVICE_UPDATED, MAP_DEVICE_REMOVED,
 *   MAP_TRACKING_GROUP_UPDATED, MAP_TRACKING_GROUP_REMOVED, MAP_REGION_UPDATED, MAP_REGION_REMOVED,
 *   MAP_ZONE_UPDATED, MAP_ZONE_REMOVED
 * - Optional: handle MAP_LIST with MAP_LIST_RESULT for initial map data over socket
 */

let activeMapJoinPayload = null
let mapJoinOnAuthRegistered = false

const registerMapJoinOnReconnect = () => {
  if (mapJoinOnAuthRegistered) return
  mapJoinOnAuthRegistered = true
  onSocketAuthenticated(() => {
    if (activeMapJoinPayload != null) {
      queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.MAP_JOIN, activeMapJoinPayload)
    }
  })
}

const emitWithAck = async (event, payload, resultEvent) => {
  const socket = await connectMainSocket()
  if (!socket) {
    return Promise.reject(new Error('Socket not connected'))
  }
  return new Promise((resolve, reject) => {
    const handleResult = data => {
      socket.off(resultEvent, handleResult)
      resolve(data)
    }
    socket.on(resultEvent, handleResult)
    queueEmitUntilConnected(event, payload || {})
  })
}

/**
 * Join the map room so the server can push real-time map updates.
 * Re-joins on reconnect via queueEmitUntilConnected.
 * @param {Object} [payload] - Optional context, e.g. { userId, deviceId }
 */
const joinMapContext = async (payload = {}) => {
  activeMapJoinPayload = payload
  registerMapJoinOnReconnect()
  await connectMainSocket()
  queueEmitUntilConnected(MAIN_SOCKET_API_EVENTS.MAP_JOIN, payload)
}

/**
 * Leave the map room.
 * @param {Object} [payload] - Same shape as join (optional)
 */
const leaveMapContext = async (payload = {}) => {
  activeMapJoinPayload = null
  const socket = await connectMainSocket()
  socket.emit(MAIN_SOCKET_API_EVENTS.MAP_LEAVE, payload)
}

/**
 * Subscribe to real-time device position updates.
 * Payload: { deviceId, coordinate } where coordinate is normalized REST shape (id, date, lat, lng, speed, direction, deviceId, ...).
 */
const onDevicePositionUpdated = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_POSITION_UPDATED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_POSITION_UPDATED, callback)
  })
}

/**
 * Legacy push: raw coordinate row with `uuid_device`, `lat`, `lng`, etc.
 * Payload may be the object or ["coordinates created", { ... }].
 */
const onCoordinatesCreated = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(coordinatesCreatedEvent)
    socket.on(coordinatesCreatedEvent, callback)
  })
}

/**
 * Subscribe to device updated (full device normalized REST shape).
 */
const onDeviceUpdated = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_UPDATED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_UPDATED, callback)
  })
}

/**
 * Subscribe to device removed. Payload: { deviceId }
 */
const onDeviceRemoved = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_REMOVED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_REMOVED, callback)
  })
}

/**
 * Subscribe to tracking group updated (full tracking group normalized REST shape).
 */
const onTrackingGroupUpdated = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_TRACKING_GROUP_UPDATED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_TRACKING_GROUP_UPDATED, callback)
  })
}

/**
 * Subscribe to tracking group removed. Payload: { trackingGroupId }
 */
const onTrackingGroupRemoved = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_TRACKING_GROUP_REMOVED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_TRACKING_GROUP_REMOVED, callback)
  })
}

/**
 * Subscribe to map region updated (full map region normalized REST shape).
 */
const onMapRegionUpdated = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_REGION_UPDATED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_REGION_UPDATED, callback)
  })
}

/**
 * Subscribe to map region removed. Payload: { mapRegionId }
 */
const onMapRegionRemoved = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_REGION_REMOVED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_REGION_REMOVED, callback)
  })
}

/**
 * Subscribe to zone created/updated/activated. Payload: { zoneId } — refetch via REST.
 */
const onZoneUpdated = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_ZONE_UPDATED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_ZONE_UPDATED, callback)
  })
}

/**
 * Subscribe to zone removed. Payload: { zoneId }
 */
const onZoneRemoved = callback => {
  connectMainSocket().then(() => {
    const socket = getMainSocket()
    socket.off(MAIN_SOCKET_API_EVENTS.MAP_ZONE_REMOVED)
    socket.on(MAIN_SOCKET_API_EVENTS.MAP_ZONE_REMOVED, callback)
  })
}

/**
 * Optional: request full map data over socket (initial load).
 * Server must respond with MAP_LIST_RESULT: { devices?, deviceCoordinates?, trackingGroups?, mapRegions? } (normalized REST shapes).
 */
const listMapData = async () =>
  emitWithAck(
    MAIN_SOCKET_API_EVENTS.MAP_LIST,
    {},
    MAIN_SOCKET_API_EVENTS.MAP_LIST_RESULT
  )

/**
 * Remove all map-related listeners from the socket. Call when leaving the map screen.
 */
const removeAllMapListeners = () => {
  const socket = getMainSocket()
  if (!socket) return
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_POSITION_UPDATED)
  socket.off(MAIN_SOCKET_API_EVENTS.COORDINATES_CREATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_UPDATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_DEVICE_REMOVED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_TRACKING_GROUP_UPDATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_TRACKING_GROUP_REMOVED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_REGION_UPDATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_REGION_REMOVED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_ZONE_UPDATED)
  socket.off(MAIN_SOCKET_API_EVENTS.MAP_ZONE_REMOVED)
}

const MapSocket = {
  joinMapContext,
  leaveMapContext,
  onDevicePositionUpdated,
  onCoordinatesCreated,
  onDeviceUpdated,
  onDeviceRemoved,
  onTrackingGroupUpdated,
  onTrackingGroupRemoved,
  onMapRegionUpdated,
  onMapRegionRemoved,
  onZoneUpdated,
  onZoneRemoved,
  listMapData,
  removeAllMapListeners
}

export default MapSocket
